import { useMemo } from "react";
import { Space } from "antd";

export interface PeriodOption {
  key: string;
  label: string;
}

/**
 * NC state fiscal year runs July 1 → June 30. FY2026 starts 2025-07-01.
 * Quarter 1 = Jul–Sep, Q2 = Oct–Dec, Q3 = Jan–Mar, Q4 = Apr–Jun.
 */
export function fiscalYearFor(date: Date = new Date()): number {
  const month = date.getMonth(); // 0-indexed
  const year = date.getFullYear();
  return month >= 6 ? year + 1 : year;
}

function fiscalQuarterFor(date: Date = new Date()): { fy: number; q: number } {
  const fy = fiscalYearFor(date);
  const month = date.getMonth();
  let q: number;
  if (month >= 6 && month <= 8) q = 1;
  else if (month >= 9 && month <= 11) q = 2;
  else if (month >= 0 && month <= 2) q = 3;
  else q = 4;
  return { fy, q };
}

function decrementQuarter(fy: number, q: number): { fy: number; q: number } {
  if (q === 1) return { fy: fy - 1, q: 4 };
  return { fy, q: q - 1 };
}

export function buildPeriodOptions(now: Date = new Date()): PeriodOption[] {
  const currentFy = fiscalYearFor(now);
  const current = fiscalQuarterFor(now);
  const prior1 = decrementQuarter(current.fy, current.q);
  const prior2 = decrementQuarter(prior1.fy, prior1.q);
  const prior3 = decrementQuarter(prior2.fy, prior2.q);
  return [
    {
      key: `fy${current.fy}q${current.q}`,
      label: `FY${String(current.fy).slice(2)} Q${current.q}`,
    },
    { key: `fy${prior1.fy}q${prior1.q}`, label: `FY${String(prior1.fy).slice(2)} Q${prior1.q}` },
    { key: `fy${prior2.fy}q${prior2.q}`, label: `FY${String(prior2.fy).slice(2)} Q${prior2.q}` },
    { key: `fy${prior3.fy}q${prior3.q}`, label: `FY${String(prior3.fy).slice(2)} Q${prior3.q}` },
    { key: `fy${currentFy}`, label: `FY${String(currentFy).slice(2)}` },
    { key: "ytd", label: "Calendar YTD" },
    { key: "all", label: "All-time" },
  ];
}

interface PeriodChipsProps {
  value: string;
  onChange: (next: string) => void;
}

export default function PeriodChips({ value, onChange }: PeriodChipsProps) {
  const options = useMemo(() => buildPeriodOptions(), []);
  return (
    <Space size={6} wrap role="radiogroup" aria-label="Time period">
      {options.map((opt) => {
        const selected = opt.key === value;
        return (
          <button
            key={opt.key}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(opt.key)}
            className="apex-exec-chip"
            data-selected={selected ? "true" : "false"}
            style={{
              padding: "4px 12px",
              borderRadius: 999,
              fontSize: 13,
              fontWeight: 600,
              letterSpacing: "0.02em",
              cursor: "pointer",
              border: selected
                ? "1px solid #092940"
                : "1px solid rgba(9, 41, 64, 0.18)",
              background: selected ? "#092940" : "#fff",
              color: selected ? "#fff" : "#092940",
              transition: "background 120ms ease, color 120ms ease",
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </Space>
  );
}
